import React, { useState, useEffect } from 'react';
import config from '../../config';
import './InventoryReceiving.css';

interface ReceivingOrder {
  id: string;
  orderNumber: string;
  supplier: {
    id: string;
    name: string;
  };
  status: string;
  expectedDate: string;
  receivedDate?: string;
  totalExpectedValue?: number;
  totalReceivedValue?: number;
  receivedBy?: string;
  invoiceNumber?: string;
  notes?: string;
  receivingItems: ReceivingItem[];
}

interface ReceivingItem {
  id: string;
  supplierItem: {
    id: string;
    supplierSku: string;
    supplierName: string;
    packSize: number;
    packUnit: string;
    costPerPack: number;
    item: {
      id: string;
      name: string;
      category: string;
      baseUnit: string;
    };
  };
  expectedQuantity: number;
  receivedQuantity?: number;
  unitCost?: number;
  quality?: string;
  lotNumber?: string;
  expirationDate?: string;
  storageLocation?: string;
  notes?: string;
  discrepancy: boolean;
  discrepancyReason?: string;
}

interface Supplier {
  id: string;
  name: string;
  code?: string;
}

interface InventoryReceivingProps {
  selectedStore: string;
  enterpriseId: string;
  user: any;
}

const InventoryReceiving: React.FC<InventoryReceivingProps> = ({ selectedStore, enterpriseId, user }) => {
  const [receivingOrders, setReceivingOrders] = useState<ReceivingOrder[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeOrder, setActiveOrder] = useState<ReceivingOrder | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');

  const [createOrderForm, setCreateOrderForm] = useState({
    orderNumber: '',
    supplierId: '',
    expectedDate: '',
    invoiceNumber: '',
    notes: '',
    expectedItems: [] as any[]
  });

  useEffect(() => {
    if (selectedStore) {
      fetchReceivingOrders();
      fetchSuppliers();
    }
  }, [selectedStore, statusFilter, supplierFilter]);

  const fetchReceivingOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (supplierFilter) params.append('supplierId', supplierFilter);
      
      const response = await fetch(
        `${config.API_BASE_URL}/receiving/${selectedStore}?${params.toString()}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setReceivingOrders(data.data || []);
      } else {
        console.error('Failed to fetch receiving orders');
        setReceivingOrders([]);
      }
    } catch (error) {
      console.error('Error fetching receiving orders:', error);
      setReceivingOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/inventory/suppliers/${enterpriseId}`);
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data || []);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const createReceivingOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${config.API_BASE_URL}/receiving`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...createOrderForm,
          storeId: selectedStore,
        }),
      });

      if (response.ok) {
        await fetchReceivingOrders();
        setShowCreateModal(false);
        resetCreateForm();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error creating receiving order:', error);
      alert('Error creating receiving order');
    }
  };

  const startReceiving = async (orderId: string) => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/receiving/${orderId}/start`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          receivedBy: user.name || user.email,
        }),
      });

      if (response.ok) {
        await fetchReceivingOrders();
        const data = await response.json();
        setActiveOrder(data.data);
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error starting receiving:', error);
      alert('Error starting receiving process');
    }
  };

  const updateReceivingItem = async (itemId: string, itemData: any) => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/receiving-item/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(itemData),
      });

      if (response.ok) {
        // Refresh the active order
        if (activeOrder) {
          const orderResponse = await fetch(`${config.API_BASE_URL}/receiving-order/${activeOrder.id}`);
          if (orderResponse.ok) {
            const orderData = await orderResponse.json();
            setActiveOrder(orderData.data);
          }
        }
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error updating receiving item:', error);
      alert('Error updating item');
    }
  };

  const completeReceiving = async (orderId: string) => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/receiving/${orderId}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        await fetchReceivingOrders();
        setActiveOrder(null);
        const data = await response.json();
        alert(data.hasDiscrepancies ? 
          'Receiving completed with discrepancies. Please review.' : 
          'Receiving completed successfully!');
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error completing receiving:', error);
      alert('Error completing receiving');
    }
  };

  const resetCreateForm = () => {
    setCreateOrderForm({
      orderNumber: '',
      supplierId: '',
      expectedDate: '',
      invoiceNumber: '',
      notes: '',
      expectedItems: []
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'receiving': return '#3b82f6';
      case 'completed': return '#10b981';
      case 'discrepancy': return '#ef4444';
      case 'cancelled': return '#6b7280';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="receiving-loading">
        <div className="loading-spinner"></div>
        <p>Loading receiving orders...</p>
      </div>
    );
  }

  if (activeOrder) {
    return (
      <div className="inventory-receiving">
        <div className="receiving-header">
          <div className="header-info">
            <h2>Receiving Order: {activeOrder.orderNumber}</h2>
            <p>Supplier: {activeOrder.supplier.name}</p>
          </div>
          <div className="header-actions">
            <button 
              className="back-btn"
              onClick={() => setActiveOrder(null)}
            >
              ← Back to Orders
            </button>
            <button 
              className="complete-btn"
              onClick={() => completeReceiving(activeOrder.id)}
              disabled={activeOrder.receivingItems.some(item => 
                item.receivedQuantity === null || item.receivedQuantity === undefined
              )}
            >
              Complete Receiving
            </button>
          </div>
        </div>

        <div className="receiving-items">
          <h3>Items to Receive</h3>
          <div className="items-grid">
            {activeOrder.receivingItems.map((item) => (
              <ReceivingItemCard 
                key={item.id}
                item={item}
                onUpdate={(data) => updateReceivingItem(item.id, data)}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-receiving">
      {/* Header */}
      <div className="receiving-header">
        <div className="header-info">
          <h2>Inventory Receiving</h2>
          <p>Manage delivery receipts and update inventory levels.</p>
        </div>
        <button 
          className="create-order-btn" 
          onClick={() => setShowCreateModal(true)}
        >
          ➕ Create Receiving Order
        </button>
      </div>

      {/* Filters */}
      <div className="receiving-filters">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="receiving">Receiving</option>
          <option value="completed">Completed</option>
          <option value="discrepancy">Discrepancy</option>
          <option value="cancelled">Cancelled</option>
        </select>
        
        <select
          value={supplierFilter}
          onChange={(e) => setSupplierFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Suppliers</option>
          {suppliers.map(supplier => (
            <option key={supplier.id} value={supplier.id}>
              {supplier.name}
            </option>
          ))}
        </select>
      </div>

      {/* Receiving Orders List */}
      <div className="receiving-orders">
        {receivingOrders.length === 0 ? (
          <div className="empty-receiving">
            <h3>No Receiving Orders</h3>
            <p>Create a receiving order to start processing deliveries.</p>
            <button 
              className="create-first-order-btn"
              onClick={() => setShowCreateModal(true)}
            >
              Create Your First Receiving Order
            </button>
          </div>
        ) : (
          <div className="orders-grid">
            {receivingOrders.map((order) => (
              <div key={order.id} className="order-card">
                <div className="order-header">
                  <div className="order-info">
                    <h3>{order.orderNumber}</h3>
                    <p>{order.supplier.name}</p>
                  </div>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(order.status) }}
                  >
                    {order.status}
                  </span>
                </div>
                
                <div className="order-details">
                  <div className="detail-row">
                    <span>Expected:</span>
                    <span>{new Date(order.expectedDate).toLocaleDateString()}</span>
                  </div>
                  {order.receivedDate && (
                    <div className="detail-row">
                      <span>Received:</span>
                      <span>{new Date(order.receivedDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span>Items:</span>
                    <span>{order.receivingItems.length}</span>
                  </div>
                  {order.totalExpectedValue && (
                    <div className="detail-row">
                      <span>Expected Value:</span>
                      <span>{formatCurrency(order.totalExpectedValue)}</span>
                    </div>
                  )}
                </div>

                <div className="order-actions">
                  {order.status === 'pending' && (
                    <button 
                      className="start-btn"
                      onClick={() => startReceiving(order.id)}
                    >
                      Start Receiving
                    </button>
                  )}
                  {order.status === 'receiving' && (
                    <button 
                      className="continue-btn"
                      onClick={() => setActiveOrder(order)}
                    >
                      Continue Receiving
                    </button>
                  )}
                  <button 
                    className="view-btn"
                    onClick={() => setActiveOrder(order)}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Order Modal */}
      {showCreateModal && (
        <CreateReceivingOrderModal
          suppliers={suppliers}
          formData={createOrderForm}
          onChange={setCreateOrderForm}
          onSubmit={createReceivingOrder}
          onCancel={() => {
            setShowCreateModal(false);
            resetCreateForm();
          }}
        />
      )}
    </div>
  );
};

// Receiving Item Card Component
interface ReceivingItemCardProps {
  item: ReceivingItem;
  onUpdate: (data: any) => void;
}

const ReceivingItemCard: React.FC<ReceivingItemCardProps> = ({ item, onUpdate }) => {
  const [formData, setFormData] = useState({
    receivedQuantity: item.receivedQuantity?.toString() || '',
    quality: item.quality || 'good',
    lotNumber: item.lotNumber || '',
    expirationDate: item.expirationDate || '',
    storageLocation: item.storageLocation || '',
    notes: item.notes || '',
    discrepancyReason: item.discrepancyReason || ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate({
      receivedQuantity: parseFloat(formData.receivedQuantity) || 0,
      quality: formData.quality,
      lotNumber: formData.lotNumber,
      expirationDate: formData.expirationDate,
      storageLocation: formData.storageLocation,
      notes: formData.notes,
      discrepancyReason: formData.discrepancyReason
    });
  };

  const hasDiscrepancy = formData.receivedQuantity && 
    Math.abs(parseFloat(formData.receivedQuantity) - item.expectedQuantity) > 0.001;

  return (
    <div className={`receiving-item-card ${item.receivedQuantity !== null ? 'received' : ''}`}>
      <div className="item-header">
        <h4>{item.supplierItem.item.name}</h4>
        <span className="supplier-sku">{item.supplierItem.supplierSku}</span>
      </div>
      
      <div className="item-expected">
        <span>Expected: {item.expectedQuantity} {item.supplierItem.packUnit}</span>
        {item.unitCost && (
          <span>Unit Cost: ${item.unitCost.toFixed(2)}</span>
        )}
      </div>

      <form onSubmit={handleSubmit} className="receiving-form">
        <div className="form-row">
          <div className="form-group">
            <label>Received Quantity *</label>
            <input
              type="number"
              value={formData.receivedQuantity}
              onChange={(e) => setFormData(prev => ({ ...prev, receivedQuantity: e.target.value }))}
              step="0.001"
              min="0"
              required
            />
          </div>
          
          <div className="form-group">
            <label>Quality</label>
            <select
              value={formData.quality}
              onChange={(e) => setFormData(prev => ({ ...prev, quality: e.target.value }))}
            >
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Lot Number</label>
            <input
              type="text"
              value={formData.lotNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, lotNumber: e.target.value }))}
              placeholder="Batch/Lot ID"
            />
          </div>
          
          <div className="form-group">
            <label>Expiration Date</label>
            <input
              type="date"
              value={formData.expirationDate}
              onChange={(e) => setFormData(prev => ({ ...prev, expirationDate: e.target.value }))}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Storage Location</label>
          <input
            type="text"
            value={formData.storageLocation}
            onChange={(e) => setFormData(prev => ({ ...prev, storageLocation: e.target.value }))}
            placeholder="e.g., Freezer A, Shelf 3"
          />
        </div>

        {hasDiscrepancy && (
          <div className="form-group discrepancy">
            <label>Discrepancy Reason *</label>
            <textarea
              value={formData.discrepancyReason}
              onChange={(e) => setFormData(prev => ({ ...prev, discrepancyReason: e.target.value }))}
              placeholder="Explain the quantity difference..."
              required
            />
          </div>
        )}

        <div className="form-group">
          <label>Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            rows={2}
            placeholder="Additional notes..."
          />
        </div>

        <button type="submit" className="receive-btn">
          {item.receivedQuantity !== null ? 'Update' : 'Receive'} Item
        </button>
      </form>
    </div>
  );
};

// Create Receiving Order Modal Component
interface CreateReceivingOrderModalProps {
  suppliers: Supplier[];
  formData: any;
  onChange: (data: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

const CreateReceivingOrderModal: React.FC<CreateReceivingOrderModalProps> = ({
  suppliers,
  formData,
  onChange,
  onSubmit,
  onCancel
}) => {
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Create Receiving Order</h3>
          <button className="close-btn" onClick={onCancel}>✕</button>
        </div>
        
        <form onSubmit={onSubmit} className="receiving-order-form">
          <div className="form-group">
            <label>Order Number *</label>
            <input
              type="text"
              value={formData.orderNumber}
              onChange={(e) => onChange({ ...formData, orderNumber: e.target.value })}
              required
              placeholder="PO-001"
            />
          </div>

          <div className="form-group">
            <label>Supplier *</label>
            <select
              value={formData.supplierId}
              onChange={(e) => onChange({ ...formData, supplierId: e.target.value })}
              required
            >
              <option value="">Select supplier</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Expected Date</label>
              <input
                type="date"
                value={formData.expectedDate}
                onChange={(e) => onChange({ ...formData, expectedDate: e.target.value })}
              />
            </div>
            
            <div className="form-group">
              <label>Invoice Number</label>
              <input
                type="text"
                value={formData.invoiceNumber}
                onChange={(e) => onChange({ ...formData, invoiceNumber: e.target.value })}
                placeholder="INV-12345"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => onChange({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Additional notes..."
            />
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="create-btn">
              Create Order
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryReceiving;